import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { CheckCircleOutline as DoneIcon, Telegram as TelegramIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useFinancialContexts } from '../../contexts/ContextsContext';
import { FinancialContext, contextApi } from '../../services/contextApi';
import { botAddToGroupUrl, NEW_TELEGRAM_GROUP_URL, telegramShareUrl } from '../../config/telegram';
import { tokens } from '../../theme/tokens';

interface Props {
  open: boolean;
  context: FinancialContext | null;
  onClose: () => void;
}

/** How often to look for the link while the person is in Telegram, and for how long. */
const POLL_MS = 3000;
const POLL_LIMIT_MS = 10 * 60 * 1000;

type LinkedChat = { title: string | null; inviteLink: string | null };

const linkedChatOf = (context?: FinancialContext): LinkedChat | null => {
  const chat = context?.telegramChats?.[0];
  return chat ? { title: chat.title, inviteLink: chat.inviteLink ?? null } : null;
};

/**
 * A group's way into Telegram, right after it is created here (or later,
 * from its card): Telegram's own "new group" screen to pick people from
 * the contacts, then Fin added with a link that names this group, so it
 * links itself with no question asked. The dialog waits for that, then
 * offers to invite more people through Telegram's share picker.
 */
const ConnectTelegramDialog: React.FC<Props> = ({ open, context, onClose }) => {
  const { t } = useTranslation('contexts');
  const navigate = useNavigate();
  const { state } = useAuth();
  const { refreshQuietly } = useFinancialContexts();
  const telegramLinked = Boolean(state.user?.isTelegramLinked);
  const [asAdmin, setAsAdmin] = useState(true);
  const [waiting, setWaiting] = useState(false);
  const [linkedChat, setLinkedChat] = useState<LinkedChat | null>(null);
  const waitingSince = useRef<number>(0);

  // A fresh start each time it opens, or for another group; not on every
  // refresh of the list, which hands in a new object for the same group.
  const contextId = context?.id;
  useEffect(() => {
    if (!open) return;
    setAsAdmin(true);
    setWaiting(false);
    setLinkedChat(null);
  }, [open, contextId]);

  // The group's own data says when Fin linked it, however that arrived.
  useEffect(() => {
    const found = linkedChatOf(context ?? undefined);
    if (open && found) {
      setLinkedChat(found);
      setWaiting(false);
    }
  }, [open, context]);

  const check = useCallback(async () => {
    if (!context) return;
    try {
      const fresh = await refreshQuietly();
      const found = linkedChatOf(fresh.find((candidate) => candidate.id === context.id));
      if (found) {
        setLinkedChat(found);
        setWaiting(false);
      }
    } catch {
      // Offline for a moment: the next tick tries again.
    }
  }, [context, refreshQuietly]);

  // While the person is in Telegram adding Fin, look for the link; coming
  // back to this tab looks at once.
  useEffect(() => {
    if (!open || !waiting) return undefined;
    const timer = window.setInterval(() => {
      if (Date.now() - waitingSince.current > POLL_LIMIT_MS) {
        setWaiting(false);
        return;
      }
      check();
    }, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [open, waiting, check]);

  if (!context) return null;

  // "I am about to add Fin for this group": Telegram does not always tell Fin
  // which group the link is for (the iPhone app drops it when adding Fin as
  // an admin), so the server links the group this person adds Fin to next.
  // Sent without waiting: an await before Telegram opens would lose the tap
  // on iOS, and the link still works without it.
  const markIntent = () => {
    if (context) contextApi.markTelegramIntent(context.id).catch(() => undefined);
  };

  const startWaiting = () => {
    waitingSince.current = Date.now();
    setWaiting(true);
  };

  const step = (number: number, title: string, text: string, action: React.ReactNode) => (
    <Box component='li' sx={{ display: 'flex', gap: 1.5 }}>
      <Box
        component='span'
        aria-hidden
        sx={{ width: 30, height: 30, flexShrink: 0, borderRadius: '50%', bgcolor: tokens.accentSoft, color: tokens.accent, fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        {number}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <strong>{title}</strong>
          <Box component='span' sx={{ fontSize: 14, color: tokens.ink2, lineHeight: 1.5 }}>{text}</Box>
        </Box>
        {action}
      </Box>
    </Box>
  );

  const body = (() => {
    if (!telegramLinked) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity='info'>{t('connect.linkFirst')}</Alert>
          <Button
            variant='contained'
            startIcon={<TelegramIcon />}
            onClick={() => {
              onClose();
              navigate('/settings/telegram');
            }}
            sx={{ alignSelf: 'flex-start', minHeight: 44 }}
          >
            {t('connect.linkFirstButton')}
          </Button>
        </Box>
      );
    }

    if (linkedChat) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box role='status' sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', p: 1.75, borderRadius: '14px', bgcolor: tokens.accentSoft }}>
            <DoneIcon sx={{ color: tokens.accent, mt: '1px' }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <strong>{t('connect.linked', { name: context.name, chat: linkedChat.title ?? context.name })}</strong>
              <Box component='span' sx={{ fontSize: 14, color: tokens.ink2 }}>
                {t('connect.linkedHint')}
              </Box>
            </Box>
          </Box>
          {linkedChat.inviteLink ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Button
                variant='contained'
                startIcon={<TelegramIcon />}
                href={telegramShareUrl(linkedChat.inviteLink, t('connect.inviteText', { name: context.name }))}
                target='_blank'
                rel='noopener noreferrer'
                sx={{ alignSelf: 'flex-start', minHeight: 44 }}
              >
                {t('connect.invite')}
              </Button>
              <Box component='span' sx={{ fontSize: 13, color: tokens.ink3 }}>{t('connect.inviteHint')}</Box>
            </Box>
          ) : (
            <Box component='p' sx={{ m: 0, fontSize: 14, color: tokens.ink2 }}>{t('connect.inviteNeedsAdmin')}</Box>
          )}
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box component='p' sx={{ m: 0, color: tokens.ink2, lineHeight: 1.5 }}>{t('connect.lead')}</Box>
        <Box>
          <FormControlLabel
            control={<Switch checked={asAdmin} onChange={(event) => setAsAdmin(event.target.checked)} />}
            label={t('connect.admin')}
          />
          <Box component='span' sx={{ display: 'block', fontSize: 13, color: tokens.ink3, ml: 0.5 }}>{t('connect.adminHint')}</Box>
        </Box>
        <Box component='ol' sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 2.25 }}>
          {step(
            1,
            t('connect.step1'),
            t('connect.step1Text'),
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Button variant='outlined' href={NEW_TELEGRAM_GROUP_URL} onClick={markIntent} sx={{ alignSelf: 'flex-start', minHeight: 44 }}>
                {t('connect.step1Button')}
              </Button>
              <Box component='span' sx={{ fontSize: 12.5, color: tokens.ink3 }}>{t('connect.step1Fallback')}</Box>
            </Box>,
          )}
          {step(
            2,
            t('connect.step2'),
            t('connect.step2Text', { name: context.name }),
            <Button
              variant='contained'
              startIcon={<TelegramIcon />}
              href={botAddToGroupUrl(context.id, { asAdmin })}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() => {
                markIntent();
                startWaiting();
              }}
              sx={{ alignSelf: 'flex-start', minHeight: 44 }}
            >
              {t('connect.step2Button')}
            </Button>,
          )}
        </Box>
        {waiting && (
          <Box role='status' sx={{ display: 'flex', alignItems: 'center', gap: 1.25, color: tokens.ink2, fontSize: 14 }}>
            <CircularProgress size={18} />
            {t('connect.waiting')}
          </Box>
        )}
      </Box>
    );
  })();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm' aria-labelledby='connect-telegram-title'>
      <DialogTitle id='connect-telegram-title' sx={{ fontFamily: tokens.display, fontWeight: 700 }}>
        {t('connect.title', { name: context.name })}
      </DialogTitle>
      <DialogContent>{body}</DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {linkedChat ? (
          <Button variant='contained' onClick={onClose}>{t('connect.done')}</Button>
        ) : (
          <Button onClick={onClose} sx={{ color: tokens.ink2 }}>{t('connect.later')}</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

/**
 * For a button that opens this window for the group selected in the app:
 * it stays open only while that group is selected (picking another, or the
 * group going away, closes it).
 */
export const useConnectTelegramFor = (context: FinancialContext | null | undefined) => {
  const [openFor, setOpenFor] = useState<string | null>(null);
  const contextId = context?.id ?? null;
  useEffect(() => {
    if (openFor && openFor !== contextId) setOpenFor(null);
  }, [openFor, contextId]);
  return {
    openDialog: () => setOpenFor(contextId),
    dialogProps: {
      open: openFor !== null && openFor === contextId,
      context: context ?? null,
      onClose: () => setOpenFor(null),
    },
  };
};

export default ConnectTelegramDialog;
